import React, { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';
import { Dropdown } from 'primereact/dropdown';
import { dateTimeTemplate, isPermissionCodeValid, logTypeOptions } from '../../../utils/mics';

const SysErrorCodeDetail = ({ pageId }) => {

    const readAccess = isPermissionCodeValid(`${pageId}-R`);
    const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    const deleteAccess = isPermissionCodeValid(`${pageId}-D`);
    
    const { state } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const sysErrorCode = useSelector((state) => state.sysErrorCodes.sysErrorCode);

    const [record, setRecord] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);

    useEffect(() => {
        if (!state) return;
        const { data, isView, isEdit } = state;
        setRecord(data || {});
        setIsViewMode(isView || false);
        setIsEditMode(isEdit || false);
    }, [state]);


    const handleSubmit = (data) => {
        if (data.id) {
            dispatch(appActions.SYSERRORCODE_UPDATE_REQUEST(data));
        } else {
            dispatch(appActions.SYSERRORCODE_CREATE_REQUEST(data));
        }
    };

    const handleRedirect = useCallback(() => {
        dispatch(appActions.SYSERRORCODE_RESET_REQUEST());
        navigate('/ui/system-error-codes', { replace: true });
    }, [dispatch, navigate]);

    useEffect(() => {
        if (sysErrorCode?.action === 'create' || sysErrorCode?.action === 'update') {
            handleRedirect();
        }
    }, [sysErrorCode, handleRedirect]);

    const formik = useFormik({
        initialValues: {
            id: record?.id || '',
            errorCode: record?.errorCode || '',
            errorMsg: record?.errorMsg || '',
            errorType: record?.errorType || null,
            errorGroup: record?.errorGroup || '',
            errorLevel: record?.errorLevel || '',
            alertGroup: record?.alertGroup || '',
            lastUpdatedBy: record?.lastUpdatedBy || '',
            createdOn: dateTimeTemplate(record?.createdOn) || '',
            lastUpdated: dateTimeTemplate(record?.lastUpdated) || '',
        },
        enableReinitialize: true,
        validate: (data) => {
            const requiredFields = [
                { field: 'errorCode', message: 'Error Code is required.' },
                { field: 'errorMsg', message: 'Error Message is required.' },
                { field: 'errorType', message: 'Error Type is required.' },
                { field: 'errorGroup', message: 'Error Group is required.' },
                { field: 'alertGroup', message: 'Alert Group is required.' },
                { field: 'errorLevel', message: 'Error Level is required.' }
            ];
        
            let errors = {};
        
            requiredFields.forEach(({ field, message }) => {
                if (!data[field]) {
                    errors[field] = message;
                }
            });
        
            return errors;
        },
        onSubmit: (values) => {
            const data = {
                ...values,
            };
            if (isEditMode) {
                data['id'] = record.id;
            }
            handleSubmit(data);
        },
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => {
        return (
            isFormFieldValid(name) && (
                <small className="p-error">{formik.errors[name]}</small>
            )
        );
    };

    const renderFormArea = () => {
        return (
            <div className="p-fluid formgrid grid br-2 bg-white p-2 py-2 border-round-xl shadow-1">
                {(isEditMode || isViewMode) && (
                    <div className="col-4 mb-3">
                        <label htmlFor="id">Id</label>
                        <InputText id="id" name="id" value={formik.values.id} onChange={formik.handleChange} disabled />
                    </div>
                )}
                <div className="col-4 mb-3">
                    <label htmlFor="errorCode">Error Code*</label>
                    <InputText id="errorCode" name="errorCode" value={formik.values.errorCode} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('errorCode') })} readOnly={isViewMode} />
                    {getFormErrorMessage('errorCode')}
                </div>
                
                <div className="col-4 mb-2">
                    <label htmlFor="errorMsg">Error Message*</label>
                    <InputText id="errorMsg" name="errorMsg" value={formik.values.errorMsg} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('errorMsg') })} readOnly={isViewMode} />
                    {getFormErrorMessage('errorMsg')}
                </div>
               
                <div className="col-4 mb-2">
                    <label htmlFor="errorType">Error Type*</label>
                    <Dropdown  filter showClear value={formik.values.errorType} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('errorType') })} readOnly={isViewMode} options={logTypeOptions} id="errorType"  name="errorType"  optionLabel="code" optionValue="name" />
                    {getFormErrorMessage('errorType')}
                </div>
                        
                <div className="col-4 mb-2">
                    <label htmlFor="errorGroup">Error Group*</label>
                    <InputText id="errorGroup" name="errorGroup" value={formik.values.errorGroup} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('errorGroup') })} readOnly={isViewMode} />
                    {getFormErrorMessage('errorGroup')}
                </div>

                <div className="col-4 mb-2">
                    <label htmlFor="errorLevel">Error Level*</label>
                    <InputText id="errorLevel" name="errorLevel" value={formik.values.errorLevel} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('errorLevel') })} readOnly={isViewMode} />
                    {getFormErrorMessage('errorLevel')}
                </div>

                <div className="col-4 mb-2">
                    <label htmlFor="alertGroup">Alert Group*</label>
                    <InputText id="alertGroup" name="alertGroup" value={formik.values.alertGroup} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('alertGroup') })} readOnly={isViewMode} />
                    {getFormErrorMessage('alertGroup')}
                </div>
                
               {(isEditMode || isViewMode) && (
                    <>
                        <div className="col-4 mb-3">
                            <label htmlFor="createdOn">Created On</label>
                            <InputText id="createdOn" name="createdOn" value={formik.values.createdOn} onChange={formik.handleChange} disabled/>
                        </div>
                        <div className="col-4 mb-3">
                            <label htmlFor="lastUpdated">Last Updated</label>
                            <InputText id="lastUpdated" name="lastUpdated" value={formik.values.lastUpdated} onChange={formik.handleChange} disabled />
                        </div>
                        <div className="col-4 mb-3">
                            <label htmlFor="lastUpdatedBy">Last Updated By</label>
                            <InputText id="lastUpdatedBy" name="lastUpdatedBy" value={formik.values.lastUpdatedBy} onChange={formik.handleChange} disabled />
                        </div>
                    </>
                )}
               
            </div>
        );
    };

    const renderMainButton = () => {
        return (
            <>
                {(!isViewMode && writeAccess) && 
                    <Button
                        label={isEditMode ? 'Update' : 'Save'}
                        className="p-button-success tp"
                        data-pr-tooltip={isEditMode ? 'Update' : 'Save'}
                        severity="success" outlined
                        icon="pi pi-save" type="submit"
                    />
                }
                <Button
                    label={isViewMode ? 'Back' : 'Cancel'}
                    icon={isViewMode ? 'pi pi-arrow-left' : 'pi pi-times'}
                    className="p-button-secondary tp" type="button"
                    severity="secondary" outlined
                     data-pr-tooltip={isViewMode ? 'Back' : 'Cancel'}
                    onClick={() => navigate('/ui/system-error-codes')}
                />
            </>
        );
    };

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <div className="form_height px-2 mt-2">
                    <div className="d-flex flex-row gap-10 align-items-center mb-1">
                        <h2 className="page-header mr-auto">
                            {isEditMode
                                ? 'Edit System Error Code'
                                : 'System Error Code Details'}
                        </h2>
                        {renderMainButton()}
                    </div>
                    <div className="grid px-2">
                        <div className="col-12">{renderFormArea()}</div>
                    </div>
                </div>
            </form>
        </>
    );
};

export default SysErrorCodeDetail;
